/**
 * Яндекс.Метрика: загрузка счётчика арены и отправка хитов и целей.
 *
 * Счётчик задаётся полем арены `ymId`, как `gaId` у Google Analytics, и
 * грузится только в браузере: на сервере Метрики нет, а `initializeYm`
 * вызывается из того же места, что и инициализация GA, то есть и при SSR.
 *
 * Метрика не знает про category, action и label, у неё есть хиты
 * (просмотры страниц) и цели (`reachGoal`) с произвольным идентификатором
 * и параметрами визита. Поэтому пара category и action превращается в
 * идентификатор цели (см. `goalName` в core/utils/analytics.js), а label и
 * value уезжают параметрами.
 *
 * Важно: цель засчитывается, только если она заведена в интерфейсе
 * Метрики как «JavaScript-событие» с тем же идентификатором. Незаведённая
 * цель не ломает ничего, событие просто не попадёт в отчёты по целям.
 */
const SCRIPT_URL = "https://mc.yandex.ru/metrika/tag.js";

let counterId = null;

function getYm() {
  if (!process.browser) return null;
  if (!counterId) return null;
  if (typeof window.ym !== "function") return null;

  return window.ym;
}

/**
 * Очередь `window.ym` и тег счётчика, тот же код, что Метрика даёт в
 * сниппете, только без inline-скрипта в разметке.
 */
function injectCounter() {
  if (typeof window.ym === "function") return;

  window.ym =
    window.ym ||
    function ymStub(...args) {
      (window.ym.a = window.ym.a || []).push(args);
    };
  window.ym.l = Number(new Date());

  const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`);

  if (existing) return;

  const script = document.createElement("script");

  script.async = true;
  script.src = SCRIPT_URL;

  const first = document.getElementsByTagName("script")[0];

  if (first && first.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
}

export function initializeYm(arena) {
  if (!process.browser) return;
  if (!arena?.ymId) return;
  if (counterId) return;

  injectCounter();

  counterId = arena.ymId;

  window.ym(counterId, "init", {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    // Все просмотры, включая первый, шлём руками из usePageview, когда
    // известен настоящий заголовок страницы. Автоматический хит на
    // инициализации давал второй просмотр той же страницы (глубина и
    // просмотры удваивались), а в эмбеде уходил с заголовком по
    // умолчанию до загрузки видео.
    defer: true,
  });
}

/**
 * Просмотр страницы при клиентской навигации.
 */
export function ymHit(page, title) {
  const ym = getYm();

  if (!ym) return;
  if (!page) return;

  ym(counterId, "hit", page, title ? { title } : undefined);
}

/**
 * Достижение цели. `params` уходят параметрами визита, пустые значения
 * отбрасываем, чтобы не засорять отчёты.
 */
export function ymReachGoal(name, params) {
  const ym = getYm();

  if (!ym) return;
  if (!name) return;

  const payload = Object.entries(params || {}).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === "") return acc;

    acc[key] = value;

    return acc;
  }, {});

  ym(
    counterId,
    "reachGoal",
    name,
    Object.keys(payload).length ? payload : undefined
  );
}
